import React from 'react';
import { ShieldAlert, RefreshCw } from 'lucide-react';
import { Button, Card } from '@verif-y/ui';
import { AppFooter } from '@/components/layout/app-footer';

interface AuthErrorProps {
  title?: string;
  message?: string;
  buttonLabel?: string;
  onRetry: () => void;
}

/**
 * AuthError - Reusable authentication error display.
 * WHY: Single source of truth for auth error UI across App.tsx and AuthCallback.tsx.
 */
export const AuthError: React.FC<AuthErrorProps> = ({
  title = 'Authentication Error',
  message = 'We encountered an issue while verifying your session. Please try again.',
  buttonLabel = 'Try Again',
  onRetry,
}) => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main
        role="main"
        aria-labelledby="auth-error-title"
        className="flex-1 flex items-center justify-center p-4"
      >
        <Card
          variant="glass"
          className="p-8 max-w-md w-full text-center"
          role="alert"
          aria-live="polite"
        >
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-red-100">
              <ShieldAlert
                size={48}
                className="text-red-600"
                aria-hidden="true"
              />
            </div>
          </div>

          <h1
            id="auth-error-title"
            className="text-2xl font-bold text-foreground mb-4"
          >
            {title}
          </h1>

          <p className="text-muted-foreground mb-8 leading-relaxed">
            {message}
          </p>

          <Button
            onClick={onRetry}
            aria-label={buttonLabel}
          >
            <RefreshCw size={20} aria-hidden="true" />
            {buttonLabel}
          </Button>
        </Card>
      </main>
      <AppFooter />
    </div>
  );
};

export default AuthError;
